// Shared state for the inventory reconciliation wizard. Nothing here is
// persisted — the whole count lives in memory until the operator confirms, at
// which point it becomes one POST (spec §7).
import { writable, derived, get } from "svelte/store";
import axios from "axios";
import {
  parseInput,
  buildIndexes,
  reconcile,
  stagedChanges,
  statusLabel,
  defaultAction,
} from "./engine";

export const scope = writable({ branchId: null, type: null }); // both required, manually picked
export const missAction = writable("retire"); // header enum, default Retire (spec §5)
export const entries = writable([]); // deduped tokens {value, kind, raw, count, sources:[]}
export const pasteLog = writable([]); // {source, added, dupes}
// Every blob the operator added, verbatim. This is what lands in
// raw_scan_input on the header — including the scans nothing matched (spec §3).
export const rawScanInput = writable("");
export const overrides = writable({}); // unitId -> target status
// Explicit per-row decision: 'adjust' | 'skip'; absent = undecided. Undecided
// NEVER writes — every change needs an explicit action (spec §5).
export const decisions = writable({});
export const reviewPicks = writable({}); // scan value -> unitId | 'none'
export const branchNames = writable({}); // branchId -> name, for candidate cards

// ---- transfers (spec-addendum §A/§C) --------------------------------------
// Transfer is an axis of its own beside the status decision: an outside-scope
// row can be transferred, adjusted, both, or neither. It lives here rather than
// inside `decisions` so the two stay independently readable — and so a scope
// change clears it along with everything else the operator decided (spec §2).
//
// All three maps are keyed by unit id and only ever hold *transferable* rows
// (engine `isTransferable`); the wizard offers the affordance nowhere else.
export const transfers = writable({}); // unitId -> true: re-home into the scope branch
// The subset of `transfers` the bulk banner staged, so its Undo reverts only
// its own work and leaves hand-toggled rows standing (§C).
export const bulkTransfers = writable({});
// Rows where staging the transfer also staged the Active default (§A1) — undoing
// the transfer takes that default back, but never an operator's own pick.
export const transferAutoSet = writable({});

// The fleet dump the engine matches against, plus its load state.
export const units = writable([]);
export const fleet = writable({ status: "idle", error: null }); // idle|loading|ready|error

const indexes = derived(units, ($units) => buildIndexes($units));

// The dump is the whole company fleet, not the selected scope (see
// ReconciliationFleetQuery): scope is applied client-side, so changing it never
// needs a second trip. One fetch per wizard session, and it is a big one.
let fleetRequested = false;

export async function ensureFleet(url, currentScope) {
  if (fleetRequested || !currentScope.branchId || !currentScope.type) return;

  fleetRequested = true;
  fleet.set({ status: "loading", error: null });
  try {
    const { data } = await axios.get(url, {
      params: { branch_id: currentScope.branchId, unit_type: currentScope.type },
    });
    units.set(data.units || []);
    fleet.set({ status: "ready", error: null });
  } catch (error) {
    fleetRequested = false; // let the operator retry
    fleet.set({
      status: "error",
      error: error?.response?.data?.error || "Could not load the units for this scope.",
    });
  }
}

// Additive input (spec §3): every paste/upload merges into one scan set,
// deduped with a running tally.
export function addScans(text, source) {
  const tokens = parseInput(text);
  let added = 0;
  let dupes = 0;
  entries.update((cur) => {
    const byValue = new Map(cur.map((e) => [e.value, e]));
    for (const t of tokens) {
      const existing = byValue.get(t.value);
      if (existing) {
        existing.count += 1;
        if (!existing.sources.includes(source)) existing.sources.push(source);
        dupes += 1;
      } else {
        const entry = { ...t, count: 1, sources: [source] };
        byValue.set(t.value, entry);
        cur.push(entry);
        added += 1;
      }
    }
    return [...cur];
  });
  pasteLog.update((log) => [...log, { source, added, dupes }]);
  rawScanInput.update((cur) => `${cur}${cur ? "\n" : ""}# ${source}\n${text.trim()}`);
  return { added, dupes };
}

// Everything the operator decided, without touching the scans themselves.
export function clearDecisions() {
  overrides.set({});
  decisions.set({});
  reviewPicks.set({});
  transfers.set({});
  bulkTransfers.set({});
  transferAutoSet.set({});
}

export function resetAll() {
  entries.set([]);
  pasteLog.set([]);
  rawScanInput.set("");
  clearDecisions();
}

// Fresh wizard: Turbo keeps this module alive across visits, so a remount has
// to start from nothing rather than inherit the last count.
export function resetWizard() {
  resetAll();
  scope.set({ branchId: null, type: null });
  missAction.set("retire");
  units.set([]);
  fleet.set({ status: "idle", error: null });
  fleetRequested = false;
}

export const hasDecisions = derived(
  [decisions, overrides, reviewPicks, transfers],
  ([$decisions, $overrides, $picks, $transfers]) =>
    Object.keys($decisions).length > 0 ||
    Object.keys($overrides).length > 0 ||
    Object.keys($picks).length > 0 ||
    Object.keys($transfers).length > 0,
);

// A scope change after edits keeps the scan set but clears every decision
// (spec §2) — behind a confirm, because it throws the operator's work away.
// Returns false when they back out, so the picker can put its select back.
export function changeScope(patch) {
  const current = get(scope);
  const next = { ...current, ...patch };
  if (next.branchId === current.branchId && next.type === current.type) return true;

  if (get(hasDecisions)) {
    const proceed = window.confirm(
      "Changing the scope clears the decisions you have made so far. Your scans are kept. Continue?",
    );
    if (!proceed) return false;
    clearDecisions();
  }

  scope.set(next);
  return true;
}

export const result = derived(
  [entries, scope, reviewPicks, units, indexes],
  ([$entries, $scope, $picks, $units, $idx]) =>
    reconcile({ entries: $entries, scope: $scope, units: $units, idx: $idx, reviewPicks: $picks }),
);

export const changes = derived(
  [result, overrides, decisions, missAction],
  ([$result, $overrides, $decisions, $missAction]) =>
    stagedChanges({
      result: $result,
      overrides: $overrides,
      decisions: $decisions,
      missAction: $missAction,
    }),
);

export function decide(unitIds, decision) {
  decisions.update((d) => {
    const next = { ...d };
    for (const id of unitIds) {
      if (decision) next[id] = decision;
      else delete next[id];
    }
    return next;
  });
}

// Picking a target status is itself an "adjust" decision.
export function setOverride(unitId, status) {
  overrides.update((o) => {
    const next = { ...o };
    if (status) next[unitId] = status;
    else delete next[unitId];
    return next;
  });
  if (status) decide([unitId], "adjust");
}

// ---- transfer actions -----------------------------------------------------

// A scanned unit is physically here, so a transferred row defaults to Active
// (spec-addendum §A1) — the one place outside-scope rows get a suggestion.
const TRANSFER_STATUS = "active";

const withKeys = (map, ids, value) => {
  const next = { ...map };
  for (const id of ids) next[id] = value;
  return next;
};

const withoutKeys = (map, ids) => {
  const next = { ...map };
  for (const id of ids) delete next[id];
  return next;
};

// The single writer for all three transfer maps, batched so a bulk action costs
// one update per store rather than one per row.
//
// Staging a transfer also stages the Active default, but only on rows the
// operator has not decided yet; unstaging takes that default back, but only
// while it is still exactly as staged — a hand-picked status survives both.
function updateTransfers(unitIds, staging, { alsoActive = true, bulk = false } = {}) {
  const $transfers = get(transfers);
  const touched = unitIds.filter((id) => Boolean($transfers[id]) !== staging);
  if (touched.length === 0) return;

  const $decisions = get(decisions);
  const $overrides = get(overrides);
  const $autoSet = get(transferAutoSet);
  const autoOn = staging && alsoActive ? touched.filter((id) => !$decisions[id]) : [];
  const autoOff = staging
    ? []
    : touched.filter(
        (id) =>
          $autoSet[id] && $decisions[id] === "adjust" && $overrides[id] === TRANSFER_STATUS,
      );

  transfers.update((t) => (staging ? withKeys(t, touched, true) : withoutKeys(t, touched)));
  // Undo only ever owns rows the bulk staged and nobody has since unstaged.
  bulkTransfers.update((b) =>
    staging && bulk ? withKeys(b, touched, true) : withoutKeys(b, touched),
  );
  transferAutoSet.update((a) => (staging ? withKeys(a, autoOn, true) : withoutKeys(a, touched)));

  if (autoOn.length > 0) {
    overrides.update((o) => withKeys(o, autoOn, TRANSFER_STATUS));
    decisions.update((d) => withKeys(d, autoOn, "adjust"));
  }
  if (autoOff.length > 0) {
    overrides.update((o) => withoutKeys(o, autoOff));
    decisions.update((d) => withoutKeys(d, autoOff));
  }
}

// Per-row "Transfer here" (§C). Only ever called for transferable rows.
export const toggleTransfer = (unitId) => updateTransfers([unitId], !get(transfers)[unitId]);

// The banner's [Transfer all N here]; `alsoActive` is its checkbox.
export const transferAll = (unitIds, alsoActive) =>
  updateTransfers(unitIds, true, { alsoActive, bulk: true });

// The banner's [Undo bulk transfer] — surgical by construction: it walks the
// rows the bulk staged, so hand-toggled rows are not in the list at all.
export const undoBulkTransfer = () => updateTransfers(Object.keys(get(bulkTransfers)), false);

// unitId | 'none' picks; undefined undoes (the tombstone's Undo).
export function pickReview(value, unitIdOrNone) {
  reviewPicks.update((p) => {
    const next = { ...p };
    if (unitIdOrNone === undefined) delete next[value];
    else next[value] = unitIdOrNone;
    return next;
  });
}

// What the Confirm step reports back: the status transitions about to be
// written, plus the transfer half of the confirm (§C). Derived rather than
// called, because a transfer that stages no status change (transfer + "Do not
// adjust") moves none of the numbers `changes` tracks.
export const confirmSummary = derived(
  [changes, result, transfers, decisions, overrides],
  ([$changes, $result, $transfers, $decisions, $overrides]) => {
    const byTransition = {};
    for (const c of $changes) {
      const key = `${statusLabel(c.from)} → ${statusLabel(c.to)}`;
      byTransition[key] = (byTransition[key] || 0) + 1;
    }

    const transferRows = $result.groups.outsideScope.filter((row) => $transfers[row.unit.id]);
    return {
      total: $changes.length,
      byTransition,
      transferCount: transferRows.length,
      transferActiveCount: transferRows.filter(
        (row) =>
          $decisions[row.unit.id] === "adjust" && $overrides[row.unit.id] === TRANSFER_STATUS,
      ).length,
    };
  },
);

// ---- confirm payload ------------------------------------------------------

// The preview's camelCase group keys → the persisted vocabulary on
// InventoryReconciliationUnit (spec §7). Misses split further by subgroup.
const PERSISTED_GROUPS = {
  matched: "matched",
  reactivations: "reactivation",
  outsideScope: "outside_scope",
  onRental: "on_rental_skipped",
  misses: "miss",
};

// The groups whose rows the operator actually decides (spec §5).
const ACTIONABLE_GROUPS = new Set(["reactivations", "outsideScope", "misses"]);

// Misses split further by the subgroup the operator saw them in.
const persistedGroup = (row, groupKey) => {
  if (groupKey !== "misses") return PERSISTED_GROUPS[groupKey];
  if (!row.unit.barcode) return "miss_never_barcoded";
  if (row.unit.status === "incoming") return "miss_incoming";
  return "miss";
};

// One row per unit that appeared in the preview, in the shape the create action
// expects. `previewed_status` is the status the operator was shown — the server
// compares it against the live row and demotes stale adjusts to keep (spec §7).
// The POST itself is wired up separately; this is the payload it sends.
export function confirmRows() {
  const $result = get(result);
  const $decisions = get(decisions);
  const $overrides = get(overrides);
  const $transfers = get(transfers);
  const $missAction = get(missAction);
  const rows = [];
  const seen = new Set();

  const collect = (groupRows, groupKey) => {
    for (const row of groupRows) {
      if (seen.has(row.unit.id)) continue;
      seen.add(row.unit.id);

      // An "adjust" that would not actually move the status is recorded as the
      // decision it amounts to: the operator settled the row, nothing changes.
      // Matched and on-rental rows carry no decision control at all, so
      // confirming the count settles them — only actionable rows go undecided.
      const target = $overrides[row.unit.id] ?? defaultAction(row, groupKey, $missAction);
      const adjusts =
        $decisions[row.unit.id] === "adjust" && target && target !== row.unit.status;
      // Transfer is offered on the outside-scope card alone, and settles the row
      // by itself — the server rejects `transfer` on an undecided row (§A3), so
      // a transferred row is always sent as adjust or keep.
      const transferring = groupKey === "outsideScope" && Boolean($transfers[row.unit.id]);
      const settled =
        adjusts || $decisions[row.unit.id] || transferring || !ACTIONABLE_GROUPS.has(groupKey);

      const payloadRow = {
        rental_unit_id: row.unit.id,
        // A unit that only surfaced through a needs-review pick is filed under
        // that group, wherever it eventually landed.
        group: row.picked ? "needs_review_resolved" : persistedGroup(row, groupKey),
        decision: adjusts ? "adjust" : settled ? "keep" : "undecided",
        new_status: adjusts ? target : null,
        previewed_status: row.unit.status,
      };
      // Optional in the contract: sent only where it means something, so the
      // rest of a thousand-row payload stays as it was.
      if (transferring) payloadRow.transfer = true;
      rows.push(payloadRow);
    }
  };

  collect($result.groups.reactivations, "reactivations");
  collect($result.groups.outsideScope, "outsideScope");
  collect($result.groups.misses, "misses");
  collect($result.groups.matched, "matched");
  collect($result.groups.onRental, "onRental");

  return rows;
}

export function confirmPayload() {
  const $scope = get(scope);
  return {
    inventory_reconciliation: {
      branch_id: $scope.branchId,
      unit_type: $scope.type,
      miss_action: get(missAction),
      raw_scan_input: get(rawScanInput),
      rows: confirmRows(),
    },
  };
}

// This endpoint is an ordinary Rails action, not the API, so it is behind
// forgery protection like any form post — the token rides on the header.
const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";

const GENERIC_FAILURE = "Something went wrong applying this reconciliation. Nothing was changed.";

const failureMessages = (error) => {
  const { status, data } = error?.response || {};
  if (status === 422 && Array.isArray(data?.errors) && data.errors.length > 0) return data.errors;
  if (status === 403 || status === 401) return ["You are not allowed to apply this reconciliation."];
  if (!error?.response) return ["Could not reach the server. Your review is still here — try again."];
  return [GENERIC_FAILURE];
};

// The single POST that turns the whole review into an InventoryReconciliation
// (spec §7). Resolves rather than throws: a failed confirm has to leave the
// wizard's state untouched so the operator can fix it and try again.
//
// Returns {ok: true, id, url, skippedUnits, demotedTransfers} or
// {ok: false, errors: [...]}.
export async function confirmReconciliation(url) {
  let response;
  try {
    response = await axios.post(url, confirmPayload(), {
      headers: { "X-CSRF-Token": csrfToken() },
    });
  } catch (error) {
    return { ok: false, errors: failureMessages(error) };
  }

  // An unauthorized POST is answered with a redirect to the app's root, which
  // the browser follows for us — so anything that isn't a 201 naming the new
  // reconciliation counts as a failure, not just a rejected promise.
  if (response.status !== 201 || !response.data?.url) {
    return { ok: false, errors: [GENERIC_FAILURE] };
  }

  return {
    ok: true,
    id: response.data.id,
    url: response.data.url,
    // Adjusts the server refused because the unit had moved on since the
    // preview; the show page's flash names them (spec §7).
    skippedUnits: response.data.skipped_units || [],
    // Transfers the server demoted to no-transfer — the unit was already home,
    // or its rental SKU is taken at the destination (§A3). Named in the same
    // flash, with its own wording per reason.
    demotedTransfers: response.data.demoted_transfers || [],
  };
}
